import React, { useState } from 'react';
import Button from "../../../Components/Auth/Button";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-phone-input-2/lib/style.css";
import "react-datepicker/dist/react-datepicker.css";

// Helper for Confirm Password validation
const getConfirmPasswordError = (formik, getPasswordError) => {
    if (
        formik.touched.confirmPassword &&
        formik.values.confirmPassword &&
        formik.values.password &&
        !getPasswordError(formik.values.password) &&
        formik.values.password !== formik.values.confirmPassword
    ) {
        return "Passwords do not match.";
    }
    return formik.touched.confirmPassword && formik.errors.confirmPassword
        ? formik.errors.confirmPassword
        : "";
};

const customStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: "9999px",
        padding: "2px 6px",
        height: "42px",
        minHeight: "42px",
        borderColor: state.isFocused ? "#0081F8" : "#00000033",
        boxShadow: "none",
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
        "&:hover": { borderColor: "#0081F8" },
    }),
    menu: (base) => ({
        ...base,
        maxHeight: "200px",
        zIndex: 9999,
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
    }),
    menuList: (base) => ({
        ...base,
        maxHeight: "200px",
        "::-webkit-scrollbar": { width: "6px" },
        "::-webkit-scrollbar-track": { background: "#f1f1f1" },
        "::-webkit-scrollbar-thumb": { background: "#c1c1c1", borderRadius: "3px" },
        "::-webkit-scrollbar-thumb:hover": { background: "#a1a1a1" },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? "#0081F8"
            : state.isFocused
                ? "#e6f3ff"
                : "white",
        color: state.isSelected ? "white" : "#333",
        padding: "8px 12px",
        cursor: "pointer",
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
        "&:hover": {
            backgroundColor: state.isSelected ? "#0081F8" : "#e6f3ff",
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: "rgba(0, 0, 0, 0.6)",
        fontSize: "14px",
        fontFamily: "Inter, sans-serif",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: "#666",
        "&:hover": {
            color: "#0081F8",
        },
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
};

const FullRegistrationDialog = ({
    formik,
    handleFieldChange,
    getPasswordError,
    isSubmitting,
    optionalConsent,
    setOptionalConsent,
    whatsappConsent,
    setWhatsappConsent,
    countryOptions,
    NATIONALITY,
    setCurrentStage,
    FLOW_STAGE,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const nationalityOptions = NATIONALITY.map((nation) => ({
        label: nation.label,
        value: nation.value,
    }));

    // SVG Eye Icons from your Login Page
    const EyeIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" />
        </svg>
    );

    const EyeOffIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor" />
        </svg>
    );

    return (
        <div className="bg-white rounded-[12px] shadow-xl p-6 lg:p-8 w-full max-w-4xl mx-auto overflow-y-auto max-h-[90vh] relative">
            <div className="bg-gradient-to-r h-[6px] p-1 rounded-t-[12px] from-[#00C05B] to-[#007FFF] w-full absolute top-0 left-0 right-0"></div>

            <h2 className="text-xl font-archivo font-bold text-[#0081F8] lg:text-[24px] mt-2">
                Complete your Profile (Step 2/2)
            </h2>
            <p className="lg:text-[16px] font-inter mt-2 text-black/60 mb-3 text-sm">
                Please enter the remaining details to create your account.
            </p>

            <form
                autoComplete="off"
                onSubmit={formik.handleSubmit}
                noValidate
                className="mt-4"
            >
                {/* Inputs: Display Name, Phone, DOB */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="signup-field-group">
                        <input
                            type="text"
                            name="displayName"
                            placeholder="Display Name *"
                            value={formik.values.displayName}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 ${formik.touched.displayName && formik.errors.displayName ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.displayName && formik.errors.displayName}
                        </div>
                    </div>

                    <div className="signup-field-group">
                        <PhoneInput
                            value={formik.values.phoneNumber}
                            onChange={(phone) => formik.setFieldValue("phoneNumber", phone)}
                            onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                            enableSearch={true}
                            countryCodeEditable={true}
                            placeholder="Phone Number *"
                            containerClass={`tel-container border placeholder-black/60 w-full rounded-full focus-within:border-[#0081F8] ${
                                formik.touched.phoneNumber && formik.errors.phoneNumber ? "border-red-500" : "border-[#00000033]"
                            }`}
                            inputClass="tel-input placeholder-black/60"
                            buttonClass="tel-button"
                            inputStyle={{
                                width: '100%',
                                height: '42px',
                                borderRadius: '9999px',
                                border: 'none',
                                fontSize: '14px',
                                fontFamily: 'Inter, sans-serif',
                            }}
                            buttonStyle={{
                                border: 'none',
                                background: 'transparent',
                                borderRadius: '9999px 0 0 9999px',
                                paddingLeft: '8px'
                            }}
                            inputProps={{
                                name: "phoneNumber",
                                autoComplete: "tel",
                            }}
                        />

                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.phoneNumber && formik.errors.phoneNumber}
                        </div>
                    </div>

                    <div className="signup-field-group">
                        <DatePicker
                            selected={formik.values.dateOfBirth ? new Date(formik.values.dateOfBirth) : null}
                            onChange={(date) => {
                                if (date) {
                                    const formattedDate = date.toISOString().split('T')[0];
                                    formik.setFieldValue("dateOfBirth", formattedDate);
                                } else {
                                    formik.setFieldValue("dateOfBirth", "");
                                }
                            }}
                            onBlur={() => formik.setFieldTouched("dateOfBirth", true)}
                            placeholderText="Date of Birth *"
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}
                            showYearDropdown
                            dropdownMode="select"
                            wrapperClassName="w-full"
                            className={`custom-datepicker-input px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 rounded-full w-full focus:outline-none focus:border-[#0081F8] ${
                                formik.touched.dateOfBirth && formik.errors.dateOfBirth
                                    ? "border-red-500"
                                    : "border-[#00000033]"
                            }`}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                        </div>
                    </div>
                </div>

                {/* Address 1, Address 2, City */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                    <div className="signup-field-group">
                        <input
                            type="text"
                            name="addressLine1"
                            placeholder="Address Line 1 *"
                            value={formik.values.addressLine1}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 ${formik.touched.addressLine1 && formik.errors.addressLine1 ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.addressLine1 && formik.errors.addressLine1}
                        </div>
                    </div>

                    <div className="signup-field-group">
                        <input
                            type="text"
                            name="addressLine2"
                            placeholder="Address Line 2"
                            value={formik.values.addressLine2}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 border-[#00000033] rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <div style={{ minHeight: 18 }} className="mt-1"></div>
                    </div>

                    <div className="signup-field-group">
                        <input
                            type="text"
                            name="city"
                            placeholder="City / Town *"
                            value={formik.values.city}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 ${formik.touched.city && formik.errors.city ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.city && formik.errors.city}
                        </div>
                    </div>
                </div>

                {/* Region, Country, Nationality */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                    <div className="signup-field-group">
                        <input
                            type="text"
                            name="region"
                            placeholder="Region / State / Province *"
                            value={formik.values.region}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] text-[14px] font-inter border placeholder-black/60 ${formik.touched.region && formik.errors.region ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.region && formik.errors.region}
                        </div>
                    </div>

                    <div className="signup-field-group">
                        <Select
                            options={countryOptions}
                            value={countryOptions.find((o) => o.value === formik.values.country)}
                            onChange={(o) => formik.setFieldValue("country", o?.value || "")}
                            onBlur={() => formik.setFieldTouched("country", true)}
                            styles={customStyles}
                            placeholder="Country *"
                            menuPortalTarget={document.body}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.country && formik.errors.country}
                        </div>
                    </div>

                    <div className="signup-field-group">
                        <Select
                            options={nationalityOptions}
                            value={nationalityOptions.find((o) => o.value === formik.values.nationality)}
                            onChange={(o) => formik.setFieldValue("nationality", o?.value || "")}
                            onBlur={() => formik.setFieldTouched("nationality", true)}
                            styles={customStyles}
                            placeholder="Nationality *"
                            menuPortalTarget={document.body}
                        />
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.nationality && formik.errors.nationality}
                        </div>
                    </div>
                </div>

                {/* Password, Confirm Password */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                    <div className="signup-field-group relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password *"
                            value={formik.values.password}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] pr-10 text-[14px] font-inter border placeholder-black/60 ${formik.touched.password && getPasswordError(formik.values.password) ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <button 
                            type="button"
                            className="absolute right-4 top-2.5 text-gray-500 hover:text-gray-700" 
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {formik.touched.password && getPasswordError(formik.values.password)}
                        </div>
                    </div>

                    <div className="signup-field-group relative">
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Confirm Password *"
                            value={formik.values.confirmPassword}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className={`px-4 h-[42px] pr-10 text-[14px] font-inter border placeholder-black/60 ${getConfirmPasswordError(formik, getPasswordError) ? "border-red-500" : "border-[#00000033]"} rounded-full focus:outline-none focus:border-[#0081F8] w-full`}
                        />
                        <button 
                            type="button"
                            className="absolute right-4 top-2.5 text-gray-500 hover:text-gray-700" 
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                        <div style={{ minHeight: 18 }} className="text-red-500 text-xs px-2 mt-1">
                            {getConfirmPasswordError(formik, getPasswordError)}
                        </div>
                    </div>
                </div>

                {/* Consent Section */}
                <div className="md:col-span-3 space-y-2 mt-4 border-t pt-4">
                    <div className="flex items-start gap-2 ml-2">
                        <input
                            type="checkbox"
                            id="whatsappConsent"
                            checked={whatsappConsent}
                            onChange={(e) => setWhatsappConsent(e.target.checked)}
                            className="accent-[#0081F8] mt-1"
                        />
                        <label htmlFor="whatsappConsent" className="text-[12px] md:text-[14px] text-gray-600">
                            I would like to receive Whatsapp invite links for game updates and/or Group Discussion Chat.
                        </label>
                    </div>

                    <div className="flex items-start gap-2 ml-2">
                        <input
                            type="checkbox"
                            id="terms"
                            name="agreeToTerms"
                            checked={formik.values.agreeToTerms}
                            onChange={handleFieldChange}
                            onBlur={formik.handleBlur}
                            className="accent-[#0081F8] mt-1"
                        />
                        <label htmlFor="terms" className="text-[12px] md:text-[14px] text-gray-600">
                            By clicking 'Create’, I confirm that I am 18+ and agree to SportsPik's{" "}
                            <a href="/termsAndConditions" className="text-[#0081F8] underline">Terms & Conditions</a> and Game Rules.
                        </label>
                    </div>

                    <div className="flex items-start gap-2 ml-2">
                        <input
                            type="checkbox"
                            id="optionalConsent"
                            checked={optionalConsent}
                            onChange={(e) => setOptionalConsent(e.target.checked)}
                            className="accent-[#0081F8] mt-1"
                        />
                        <label htmlFor="optionalConsent" className="text-[12px] md:text-[14px] text-gray-600">
                            I consent to receive communications, promotions, and updates from SportsPik and selected third-party partners. I understand I may opt out at any time.
                        </label>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="flex items-center justify-between mt-6">
                    <button
                        type="button"
                        onClick={() => setCurrentStage(FLOW_STAGE.INITIAL)}
                        className="text-green-600 underline font-semibold hover:underline bg-transparent p-0"
                    >
                        &lt; Back
                    </button>
                    <Button
                        type="submit"
                        text="Create"
                        loading={isSubmitting}
                        disabled={!formik.values.agreeToTerms || isSubmitting}
                    />
                </div>
            </form>
        </div>
    );
};

export default FullRegistrationDialog;